import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { usePatchToolboxPlan } from "../../../api/mutations";
import { useGetToolboxPlan } from "../../../api/queries";
import Button from "../../../componentLibrary/Button";
import Input from "../../../componentLibrary/Input";
import Spinner from "../../../componentLibrary/Spinner";
import { useNotification } from "../../../hooks/useNotification";
import { APIError } from "../../../utils/ErrorHandling";
import TimeframeFields, { TimeframeMode } from "./components/TimeframeFields";

const cancelLinkClass =
  "inline-flex items-center justify-center font-medium h-10 text-sm rounded-md transition-all duration-150 ease-in-out whitespace-pre-line bg-transparent text-electrum hover:text-electrum-300 px-4";

export default function EditPage() {
  const { topicId, planId } = useParams<{ topicId: string; planId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useNotification();

  const [form, setForm] = useState({ goal: "", cue: "", timeframe: "" });
  const [mode, setMode] = useState<TimeframeMode>("cue");

  const {
    data: planData,
    isLoading,
    isError,
    error,
  } = useGetToolboxPlan(planId ?? "", { enabled: !!planId });

  useEffect(() => {
    if (!planData) return;
    const { goal, cue, timeframe } = planData;
    setForm({ goal, cue, timeframe });
    setMode(timeframe && !cue ? "time" : "cue");
  }, [planData]);

  const mutation = usePatchToolboxPlan({
    onSuccess: async () => {
      notify("success", "Plan updated");
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["toolboxTopicPlans", topicId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["toolboxPlan", planId],
        }),
      ]);
      navigate(`/toolboxes/${topicId}/plans/${planId}`);
    },
    onError: () =>
      notify("error", "We couldn't save your plan. Please try again."),
  });

  if (!topicId || !planId) {
    return <Navigate to="/error" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="w-8" />
      </div>
    );
  }

  const backTo = `/toolboxes/${topicId}/plans/${planId}`;

  if (isError || !planData) {
    const message =
      error instanceof APIError && error.statusCode === 404
        ? "Plan not found."
        : "Could not load plan.";
    return (
      <div className="w-full max-w-2xl mx-auto space-y-4 pt-4">
        <Link
          to={backTo}
          className="flex items-center gap-1.5 text-sm text-electrum-300 hover:text-electrum transition-colors w-fit"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back
        </Link>
        <p className="text-sm text-gray-500">{message}</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto space-y-6 pt-4">
      <Link
        to={backTo}
        className="flex items-center gap-1.5 text-sm text-electrum-300 hover:text-electrum transition-colors w-fit"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back
      </Link>
      <h1 className="text-2xl font-bold text-gray-800">Edit plan</h1>

      <div>
        <label
          htmlFor="plan-goal"
          className="block text-sm font-medium text-gray-400 mb-2"
        >
          Goal: To... (optional)
        </label>
        <Input
          id="plan-goal"
          value={form.goal}
          onChange={(e) => setForm((f) => ({ ...f, goal: e.target.value }))}
        />
      </div>

      <TimeframeFields
        mode={mode}
        onModeChange={setMode}
        cue={form.cue}
        onCueChange={(cue) => setForm((f) => ({ ...f, cue }))}
        timeframe={form.timeframe}
        onTimeframeChange={(timeframe) => setForm((f) => ({ ...f, timeframe }))}
      />

      <div>
        <p className="text-sm font-medium text-gray-400 mb-1">Tools</p>
        <p className="text-sm text-gray-400 italic">
          Tools will be added here.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          onClick={() =>
            mutation.mutate({
              planId,
              body: form,
            })
          }
          loading={mutation.isPending}
          disabled={mutation.isPending}
        >
          Save
        </Button>
        <Link to={backTo} className={cancelLinkClass}>
          Cancel
        </Link>
      </div>
    </div>
  );
}
